import type { EntityOutboxEntityType, EntityOutboxEntry, EntityOutboxOp } from '../db'
import { getOfflineDb } from '../dbFactory'
import { dexieActivityRepo } from '../repos/DexieActivityRepo'

const nowIso = (): string => new Date().toISOString()

const getActivityEntityLabel = (entityType: EntityOutboxEntityType): string => {
  if (entityType === 'farm') return 'Facility'
  if (entityType === 'productionUnit') return 'Production unit'
  if (entityType === 'task') return 'Task'
  if (entityType === 'mortalityMatrix') return 'Mortality matrix'
  if (entityType === 'sfrMatrix') return 'SFR matrix'
  return 'Lot'
}

const groupEntityIdsByType = (entries: EntityOutboxEntry[]): Map<EntityOutboxEntityType, Set<string>> => {
  const idsByType = new Map<EntityOutboxEntityType, Set<string>>()
  for (const entry of entries) {
    let set = idsByType.get(entry.entityType)
    if (!set) {
      set = new Set()
      idsByType.set(entry.entityType, set)
    }
    set.add(entry.entityId)
  }
  return idsByType
}

const chunk = <T,>(values: T[], size: number): T[][] => {
  const out: T[][] = []
  for (let i = 0; i < values.length; i += size) out.push(values.slice(i, i + size))
  return out
}

const deleteExistingForType = async (
  offlineDb: ReturnType<typeof getOfflineDb>,
  entityType: EntityOutboxEntityType,
  entityIds: Set<string>
): Promise<void> => {
  if (entityIds.size === 0) return
  const pairs = Array.from(entityIds).map((entityId) => [entityType, entityId] as [EntityOutboxEntityType, string])

  // Dexie anyOf can handle lists, but chunk to keep memory and query payload bounded.
  for (const batch of chunk(pairs, 500)) {
    const seqs = await offlineDb.entityOutbox.where('[entityType+entityId]').anyOf(batch).primaryKeys()
    const numericSeqs = seqs.filter((seq): seq is number => typeof seq === 'number')
    if (numericSeqs.length > 0) {
      await offlineDb.entityOutbox.bulkDelete(numericSeqs)
    }
  }
}

const deleteExistingForEntity = async (entityType: EntityOutboxEntityType, entityId: string) => {
  const offlineDb = getOfflineDb()
  const seqs = await offlineDb.entityOutbox
    .where('[entityType+entityId]')
    .equals([entityType, entityId])
    .primaryKeys()

  if (seqs.length === 0) return
  await offlineDb.entityOutbox.bulkDelete(seqs)
}

export const enqueueEntityChange = async (params: {
  entityType: EntityOutboxEntityType
  op: EntityOutboxOp
  entityId: string
  payload?: unknown
  reason?: string
  operatorActorId?: string
}): Promise<void> => {
  const offlineDb = getOfflineDb()
  await offlineDb.transaction('rw', offlineDb.entityOutbox, async () => {
    await deleteExistingForEntity(params.entityType, params.entityId)

    const entry: EntityOutboxEntry = {
      entityType: params.entityType,
      op: params.op,
      entityId: params.entityId,
      payload: params.payload,
      ...(params.reason ? { reason: params.reason } : {}),
      ...(params.operatorActorId ? { operatorActorId: params.operatorActorId } : {}),
      createdAt: nowIso(),
      attemptCount: 0
    }

    await offlineDb.entityOutbox.add(entry)
  })

  // Activity feed: log higher-level entity changes (avoid high-volume row tables like water/prod).
  if (params.entityType === 'farm' || params.entityType === 'productionUnit' || params.entityType === 'lot') {
    const payload = params.payload && typeof params.payload === 'object' && !Array.isArray(params.payload)
      ? (params.payload as Record<string, unknown>)
      : undefined

    const label = (() => {
      if (!payload) return ''
      if (params.entityType === 'farm') return typeof payload.name === 'string' ? payload.name : ''
      if (params.entityType === 'productionUnit') return typeof payload.name === 'string' ? payload.name : ''
      if (params.entityType === 'lot') return typeof payload.lotKey === 'string' ? payload.lotKey : ''
      return ''
    })()

    const farmId = payload && typeof payload.farmId === 'string' ? payload.farmId : undefined
    const productionUnitId = payload && typeof payload.productionUnitId === 'string' ? payload.productionUnitId : undefined

    const entityLabel = getActivityEntityLabel(params.entityType)

    const actionLabel = params.op === 'delete' ? 'deleted' : 'saved'
    const title = label ? `${entityLabel} ${actionLabel}: ${label}` : `${entityLabel} ${actionLabel}`

    void dexieActivityRepo.log({
      source: 'outbox',
      level: 'info',
      title,
      detail: 'Sync queued',
      farmId,
      productionUnitId,
      dedupeKey: `outbox:${params.entityType}:${params.op}:${params.entityId}`
    }).catch(() => {})
  }
}

export const enqueueEntityChanges = async (entries: Array<{
  entityType: EntityOutboxEntityType
  op: EntityOutboxOp
  entityId: string
  payload?: unknown
}>): Promise<void> => {
  if (entries.length === 0) return

  const offlineDb = getOfflineDb()
  const createdAt = nowIso()

  const normalized: EntityOutboxEntry[] = entries.map((entry) => ({
    entityType: entry.entityType,
    op: entry.op,
    entityId: entry.entityId,
    payload: entry.payload,
    createdAt,
    attemptCount: 0
  }))

  // Best-effort dedupe: delete existing entries for the same entityType+entityId.
  // We do this grouped by entityType to avoid N round-trips for large imports.
  const idsByType = groupEntityIdsByType(normalized)

  await offlineDb.transaction('rw', offlineDb.entityOutbox, async () => {
    for (const [entityType, ids] of idsByType.entries()) {
      await deleteExistingForType(offlineDb, entityType, ids)
    }

    await offlineDb.entityOutbox.bulkAdd(normalized)
  })
}

export const listEntityOutbox = async (limit = 50): Promise<EntityOutboxEntry[]> => (
  getOfflineDb().entityOutbox.orderBy('seq').limit(limit).toArray()
)

export const clearEntityOutbox = async (): Promise<void> => {
  await getOfflineDb().entityOutbox.clear()
}

export const clearEntityChange = async (
  entityType: EntityOutboxEntityType,
  entityId: string
): Promise<void> => {
  await deleteExistingForEntity(entityType, entityId)
}
